import { useEffect, useState, useRef } from 'react';

interface ScrollState {
  isSticky: boolean;
  hideHeader: boolean;
  scrollDirection: 'up' | 'down' | 'idle';
  scrollPosition: number;
}

interface UseNGSearchFormScrollOptions {
  enabled: boolean; // Only enabled when isNextGenHP is false
  gapBetweenHeaderAndForm?: number; // Default to 24px as mentioned in requirements
  searchFormPositionStyle?: 'absolute' | 'relative'; // Default to relative as mentioned in requirements
}

interface UseNGSearchFormScrollReturn {
  scrollState: ScrollState;
  searchFormRef: (el: HTMLDivElement | null) => void;
  headerElement: HTMLElement | null;
}

/**
 * Custom hook for controlling NGSearchForm scrolling behavior
 *
 * Automatically calculates header height by combining heights of:
 * - Element with class="header"
 * - Element with data-component-name="brand_bar"
 * - Element with data-component-name="o-book-HotelHeader-Loading"
 *
 * Rules:
 * 1. Form becomes sticky only when the header is completely hidden (scroll > headerHeight)
 * 2. Downward scroll: when header is hidden, form is sticky at the top of the screen
 * 3. Upward scroll: form remains sticky until header becomes visible again
 * 4. Initial positioning: form is positioned dynamicHeaderHeight + 24px from top
 *
 * @param options - Configuration options
 * @returns Hook state and refs including dynamically calculated initialTopPosition
 */
export const useNGSearchFormScroll = (options: UseNGSearchFormScrollOptions): UseNGSearchFormScrollReturn => {
  const { enabled = true, gapBetweenHeaderAndForm = 24, searchFormPositionStyle = 'relative' } = options;

  const [scrollState, setScrollState] = useState<ScrollState>({
    isSticky: false,
    hideHeader: false,
    scrollDirection: 'idle',
    scrollPosition: 0,
  });

  const searchFormElementRef = useRef<HTMLDivElement | null>(null);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);
  const [brandBarElement, setBrandBarElement] = useState<HTMLElement | null>(null);
  const [hotelHeaderElement, setHotelHeaderElement] = useState<HTMLElement | null>(null);
  const [dynamicHeaderHeight, setDynamicHeaderHeight] = useState<number>(50); // Default fallback
  const formInitialOffsetTop = useRef<number | null>(null);

  // Calculate initial top position: dynamicHeaderHeight + 24px gap
  const initialTopPosition = searchFormPositionStyle === 'absolute' ? dynamicHeaderHeight : 0 + gapBetweenHeaderAndForm;

  // Callback ref for the search form element
  const searchFormRef = (el: HTMLDivElement | null) => {
    searchFormElementRef.current = el;
  };

  // Find and cache header elements, and calculate combined height
  useEffect(() => {
    if (!enabled) return;

    // Function to find and set all header elements
    const findAndSetHeaderElements = () => {
      // Find header element with class="header"
      const header = document.querySelector('.header') as HTMLElement;

      // Find brand bar element with data-component-name="brand_bar"
      const brandBar = document.querySelector('[data-component-name="brand_bar"]') as HTMLElement;

      // Find hotel header element with data-component-name="o-book-HotelHeader-Loading"
      const hotelHeader = document.querySelector('[data-component-name="o-book-HotelHeader-Loading"]') as HTMLElement;

      setHeaderElement(header);
      setBrandBarElement(brandBar);
      setHotelHeaderElement(hotelHeader);

      return { header, brandBar, hotelHeader };
    };

    // Calculate combined header height from all three elements
    const calculateCombinedHeaderHeight = () => {
      const { header, brandBar, hotelHeader } = findAndSetHeaderElements();
      let totalHeight = 0;

      if (header) {
        const headerRect = header.getBoundingClientRect();
        totalHeight += headerRect.height || 0;
      }

      if (brandBar) {
        const brandBarRect = brandBar.getBoundingClientRect();
        totalHeight += brandBarRect.height || 0;
      }

      if (hotelHeader) {
        const hotelHeaderRect = hotelHeader.getBoundingClientRect();
        totalHeight += hotelHeaderRect.height || 0;
      }

      // Use fallback if no elements found
      const newHeight = totalHeight > 0 ? totalHeight : 50;
      setDynamicHeaderHeight(newHeight);

      return { header, brandBar, hotelHeader };
    };

    // Initial calculation
    const { header, brandBar, hotelHeader } = calculateCombinedHeaderHeight();

    // Update header height on window resize
    window.addEventListener('resize', calculateCombinedHeaderHeight);

    // Use ResizeObserver to watch for header size changes if available
    const resizeObservers: ResizeObserver[] = [];
    if (window.ResizeObserver) {
      if (header) {
        const headerResizeObserver = new ResizeObserver(calculateCombinedHeaderHeight);
        headerResizeObserver.observe(header);
        resizeObservers.push(headerResizeObserver);
      }

      if (brandBar) {
        const brandBarResizeObserver = new ResizeObserver(calculateCombinedHeaderHeight);
        brandBarResizeObserver.observe(brandBar);
        resizeObservers.push(brandBarResizeObserver);
      }

      if (hotelHeader) {
        const hotelHeaderResizeObserver = new ResizeObserver(calculateCombinedHeaderHeight);
        hotelHeaderResizeObserver.observe(hotelHeader);
        resizeObservers.push(hotelHeaderResizeObserver);
      }
    }

    // Use MutationObserver to watch for DOM changes and re-detect header elements
    let mutationObserver: MutationObserver | null = null;
    if (window.MutationObserver) {
      mutationObserver = new MutationObserver(mutations => {
        let shouldRecalculate = false;

        mutations.forEach(mutation => {
          // Check if nodes were added
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                // Check if the added element or its children contain our target elements
                const hasTargetElement =
                  (typeof element.matches === 'function' &&
                    (element.matches('.header') ||
                      element.matches('[data-component-name="brand_bar"]') ||
                      element.matches('[data-component-name="o-book-HotelHeader-Loading"]'))) ||
                  (typeof element.querySelector === 'function' &&
                    (element.querySelector('.header') !== null ||
                      element.querySelector('[data-component-name="brand_bar"]') !== null ||
                      element.querySelector('[data-component-name="o-book-HotelHeader-Loading"]') !== null));

                if (hasTargetElement) {
                  shouldRecalculate = true;
                }
              }
            });
          }
        });

        if (shouldRecalculate) {
          // Use a small delay to ensure the elements are fully rendered
          setTimeout(calculateCombinedHeaderHeight, 100);
        }
      });

      // Start observing the document body for changes
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener('resize', calculateCombinedHeaderHeight);
      resizeObservers.forEach(observer => observer.disconnect());
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [enabled]);

  // Helper to check if mobile search form modal is open
  const isMobileModalOpen = () => {
    const mobileSearchForm = document.querySelector('.mobile-search-form');
    return mobileSearchForm !== null && (mobileSearchForm as HTMLElement).offsetParent !== null;
  };

  // Watch for modal open/close and reset styles accordingly
  useEffect(() => {
    if (!enabled) return;

    let modalObserver: MutationObserver | null = null;

    const checkAndResetStyles = () => {
      if (searchFormElementRef.current) {
        if (isMobileModalOpen()) {
          // Reset styles when modal is open
          searchFormElementRef.current.style.marginTop = '0px';
          searchFormElementRef.current.style.transition = 'none';
          searchFormElementRef.current.style.position = '';
          searchFormElementRef.current.style.top = '';
          searchFormElementRef.current.style.left = '';
          searchFormElementRef.current.style.right = '';
          searchFormElementRef.current.style.zIndex = '';
          document.body.style.paddingTop = '';
        }
      }
    };

    // Use MutationObserver to watch for modal appearing/disappearing
    if (window.MutationObserver) {
      modalObserver = new MutationObserver(() => {
        checkAndResetStyles();
      });

      modalObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    // Initial check
    checkAndResetStyles();

    return () => {
      if (modalObserver) {
        modalObserver.disconnect();
      }
    };
  }, [enabled]);

  // Calculate initial form position
  useEffect(() => {
    if (!enabled || !searchFormElementRef.current) return;

    const updateFormOffsetTop = () => {
      if (searchFormElementRef.current) {
        // Don't apply margin-top when mobile modal is open
        if (isMobileModalOpen()) {
          return;
        }

        // Set the initial position based on dynamicHeaderHeight + 24px gap from the top
        formInitialOffsetTop.current = initialTopPosition;

        // Apply initial positioning when form is not sticky
        if (!scrollState.isSticky) {
          searchFormElementRef.current.style.marginTop = `${initialTopPosition}px`;
        }
      }
    };

    updateFormOffsetTop();

    // Recalculate on resize
    window.addEventListener('resize', updateFormOffsetTop);
    return () => window.removeEventListener('resize', updateFormOffsetTop);
  }, [enabled, initialTopPosition, scrollState.isSticky, dynamicHeaderHeight]);

  // Main scroll handler
  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      // Skip scroll handling when mobile modal is open
      if (isMobileModalOpen()) {
        return;
      }

      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollY.current;

      // Determine scroll direction
      const direction = scrollDiff > 0 ? 'down' : scrollDiff < 0 ? 'up' : 'idle';

      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Header visibility logic with buffer zone to prevent fluctuation
      // Add a small buffer (10px) to prevent flickering at the exact boundary
      const headerBuffer = 10;
      const shouldHideHeader = currentScrollY > dynamicHeaderHeight + headerBuffer;

      // Calculate if form should be sticky with the same buffer
      const shouldBeSticky = shouldHideHeader;

      // Apply header styles with synchronized timing
      const headerTransition = 'transform 0.3s ease-in-out';

      if (headerElement) {
        if (shouldHideHeader) {
          headerElement.style.transform = 'translateY(-100%)';
          headerElement.style.transition = headerTransition;
        } else {
          headerElement.style.transform = 'translateY(0)';
          headerElement.style.transition = headerTransition;
        }
      }

      if (brandBarElement) {
        if (shouldHideHeader) {
          brandBarElement.style.transform = 'translateY(-100%)';
          brandBarElement.style.transition = headerTransition;
        } else {
          brandBarElement.style.transform = 'translateY(0)';
          brandBarElement.style.transition = headerTransition;
        }
      }

      if (hotelHeaderElement) {
        if (shouldHideHeader) {
          hotelHeaderElement.style.transform = 'translateY(-100%)';
          hotelHeaderElement.style.transition = headerTransition;
        } else {
          hotelHeaderElement.style.transform = 'translateY(0)';
          hotelHeaderElement.style.transition = headerTransition;
        }
      }

      // Apply search form positioning with smooth transition
      const searchFormHeight = searchFormElementRef.current?.offsetHeight || 0;

      if (searchFormElementRef.current) {
        // Add transition for smooth animation
        searchFormElementRef.current.style.transition = 'transform 0.3s ease-in-out, margin-top 0.3s ease-in-out';

        if (shouldBeSticky && searchFormHeight > 0) {
          // Make form sticky at top
          document.body.style.paddingTop = `${searchFormHeight}px`;
          searchFormElementRef.current.style.marginTop = '0px';
          searchFormElementRef.current.style.position = 'fixed';
          searchFormElementRef.current.style.top = '0px';
          searchFormElementRef.current.style.left = '0px';
          searchFormElementRef.current.style.right = '0px';
          searchFormElementRef.current.style.zIndex = '1000';
        } else {
          // Restore normal positioning
          document.body.style.paddingTop = '';
          searchFormElementRef.current.style.marginTop = `${initialTopPosition}px`;
          searchFormElementRef.current.style.position = '';
          searchFormElementRef.current.style.top = '';
          searchFormElementRef.current.style.left = '';
          searchFormElementRef.current.style.right = '';
          searchFormElementRef.current.style.zIndex = '';
        }
      }

      setScrollState({
        isSticky: shouldBeSticky,
        hideHeader: shouldHideHeader,
        scrollDirection: direction,
        scrollPosition: currentScrollY,
      });

      lastScrollY.current = currentScrollY;

      // Set idle state after scroll stops
      scrollTimeout.current = setTimeout(() => {
        setScrollState(prev => ({
          ...prev,
          scrollDirection: 'idle',
        }));
      }, 150);
    };

    // Throttle scroll events
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });

    // Initial call to set state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Reset header styles and body padding on cleanup
      if (headerElement) {
        headerElement.style.transform = '';
        headerElement.style.transition = '';
      }

      if (brandBarElement) {
        brandBarElement.style.transform = '';
        brandBarElement.style.transition = '';
      }

      if (hotelHeaderElement) {
        hotelHeaderElement.style.transform = '';
        hotelHeaderElement.style.transition = '';
      }

      document.body.style.paddingTop = '';

      // Reset form styles on cleanup
      if (searchFormElementRef.current) {
        searchFormElementRef.current.style.marginTop = '';
        searchFormElementRef.current.style.transition = '';
        searchFormElementRef.current.style.position = '';
        searchFormElementRef.current.style.top = '';
        searchFormElementRef.current.style.left = '';
        searchFormElementRef.current.style.right = '';
        searchFormElementRef.current.style.zIndex = '';
      }
    };
  }, [enabled, headerElement, brandBarElement, hotelHeaderElement, initialTopPosition, dynamicHeaderHeight]);

  // Return disabled state when hook is not enabled
  if (!enabled) {
    return {
      scrollState: {
        isSticky: false,
        hideHeader: false,
        scrollDirection: 'idle',
        scrollPosition: 0,
      },
      searchFormRef,
      headerElement: null,
    };
  }

  return {
    scrollState,
    searchFormRef,
    headerElement,
  };
};
